import { useState } from 'react';
import { CheckCircle, AlertTriangle, TrendingUp, Lightbulb, Settings, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Mock optimization data
const mockOptimizations = {
  recommendations: [
    {
      id: '1',
      title: 'Implement Response Caching',
      description: 'Cache similar prompts to reduce API calls by 25%',
      impact: 'high',
      effort: 'medium',
      savings: 347.82,
      category: 'caching',
      implemented: false,
      details: 'Implement Redis-based caching for similar prompts with 1-hour TTL'
    },
    {
      id: '2',
      title: 'Optimize Prompt Engineering',
      description: 'Reduce average prompt length by 15% without quality loss',
      impact: 'medium',
      effort: 'low',
      savings: 156.23,
      category: 'prompts',
      implemented: false,
      details: 'Remove redundant instructions and use more concise language'
    },
    {
      id: '3',
      title: 'Switch to GPT-3.5 for Simple Tasks',
      description: 'Use cheaper model for classification and simple queries',
      impact: 'high',
      effort: 'medium',
      savings: 523.45,
      category: 'models',
      implemented: true,
      details: 'Already implemented smart model selection based on task complexity'
    },
    {
      id: '4',
      title: 'Implement Request Batching',
      description: 'Batch multiple requests to reduce overhead',
      impact: 'medium',
      effort: 'high',
      savings: 234.67,
      category: 'optimization',
      implemented: false,
      details: 'Group requests in 100ms windows to reduce API overhead'
    }
  ],
  implemented: [
    {
      title: 'Smart Model Selection',
      savings: 523.45,
      implementedDate: '2024-01-15',
      description: 'Automatically route simple tasks to cheaper models'
    },
    {
      title: 'Error Retry Optimization',
      savings: 89.23,
      implementedDate: '2024-01-12',
      description: 'Exponential backoff for retries to reduce wasted tokens'
    }
  ],
  metrics: {
    totalPotentialSavings: 1262.17,
    implementedSavings: 612.68,
    completionRate: 48.5,
    monthlyOptimization: 147.23
  }
};

interface RecommendationCardProps {
  recommendation: {
    id: string;
    title: string;
    description: string;
    impact: 'low' | 'medium' | 'high';
    effort: 'low' | 'medium' | 'high';
    savings: number;
    category: string;
    implemented: boolean;
    details: string;
  };
  onImplement?: (id: string) => void;
}

function RecommendationCard({ recommendation, onImplement }: RecommendationCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const impactColors = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-800'
  };

  const effortColors = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-red-100 text-red-800'
  };

  const categoryIcons = {
    caching: <Zap className="w-4 h-4" />,
    prompts: <Lightbulb className="w-4 h-4" />,
    models: <Settings className="w-4 h-4" />,
    optimization: <TrendingUp className="w-4 h-4" />
  };

  return (
    <Card className={`cursor-pointer transition-all ${recommendation.implemented ? 'bg-green-50 border-green-200' : 'hover:shadow-md'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="p-2 bg-gray-100 rounded-lg">
              {categoryIcons[recommendation.category as keyof typeof categoryIcons]}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <CardTitle className="text-sm font-medium">
                  {recommendation.title}
                </CardTitle>
                {recommendation.implemented && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {recommendation.description}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-green-600">
              ${recommendation.savings.toFixed(0)}
            </div>
            <div className="text-xs text-muted-foreground">
              monthly savings
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge className={impactColors[recommendation.impact]}>
              {recommendation.impact} impact
            </Badge>
            <Badge className={effortColors[recommendation.effort]}>
              {recommendation.effort} effort
            </Badge>
          </div>
          
          {isExpanded && (
            <div className="pt-2 border-t">
              <p className="text-sm text-gray-600 mb-3">
                {recommendation.details}
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Less Details' : 'More Details'}
            </Button>
            
            {!recommendation.implemented && onImplement && (
              <Button
                size="sm"
                onClick={() => onImplement(recommendation.id)}
              >
                Implement
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface OptimizationMetricProps {
  label: string;
  value: string | number;
  subtitle?: string;
  progress?: number;
  trend?: number;
}

function OptimizationMetric({ label, value, subtitle, progress, trend }: OptimizationMetricProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">{label}</div>
        {trend && (
          <div className={`text-xs flex items-center gap-1 ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <TrendingUp className="w-3 h-3" />
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      
      <div className="text-2xl font-bold">{value}</div>
      
      {subtitle && (
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      )}
      
      {progress !== undefined && (
        <Progress value={progress} className="h-2" />
      )}
    </div>
  );
}

export function CostOptimization() {
  const data = mockOptimizations;
  
  const handleImplement = (id: string) => {
    console.log('Implementing optimization:', id);
  };

  const pendingRecommendations = data.recommendations.filter(r => !r.implemented);
  const implementedRecommendations = data.recommendations.filter(r => r.implemented);

  return (
    <div className="space-y-6">
      {/* Optimization Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <OptimizationMetric
              label="Potential Savings"
              value={`$${data.metrics.totalPotentialSavings.toFixed(0)}`}
              subtitle="Total monthly opportunity"
              trend={12}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <OptimizationMetric
              label="Implemented Savings"
              value={`$${data.metrics.implementedSavings.toFixed(0)}`}
              subtitle="Currently saving"
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <OptimizationMetric
              label="Completion Rate"
              value={`${data.metrics.completionRate.toFixed(1)}%`}
              subtitle="Optimizations completed"
              progress={data.metrics.completionRate}
            />
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <OptimizationMetric
              label="This Month"
              value={`$${data.metrics.monthlyOptimization.toFixed(0)}`}
              subtitle="New optimizations"
              trend={23}
            />
          </CardContent>
        </Card>
      </div>

      {/* Optimization Recommendations */}
      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pendingRecommendations.length})
          </TabsTrigger>
          <TabsTrigger value="implemented">
            Implemented ({implementedRecommendations.length})
          </TabsTrigger>
          <TabsTrigger value="analysis">
            Cost Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium">Pending Optimizations</h3>
              <p className="text-sm text-muted-foreground">
                Implement these recommendations to save ${pendingRecommendations.reduce((sum, r) => sum + r.savings, 0).toFixed(0)} monthly
              </p>
            </div>
            <Button>
              Implement All Quick Wins
            </Button>
          </div>
          
          <div className="grid gap-4">
            {pendingRecommendations.map((recommendation) => (
              <RecommendationCard
                key={recommendation.id}
                recommendation={recommendation}
                onImplement={handleImplement}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="implemented" className="space-y-4">
          <div>
            <h3 className="text-lg font-medium">Implemented Optimizations</h3>
            <p className="text-sm text-muted-foreground">
              Successfully implemented optimizations saving ${data.metrics.implementedSavings.toFixed(0)} monthly
            </p>
          </div>
          
          <div className="grid gap-4">
            {implementedRecommendations.map((recommendation) => (
              <RecommendationCard
                key={recommendation.id}
                recommendation={recommendation}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Cost Optimization Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-red-600">$2,003.85</div>
                    <div className="text-sm text-muted-foreground">Baseline Cost</div>
                    <div className="text-xs text-gray-500">Before optimizations</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">$1,391.17</div>
                    <div className="text-sm text-muted-foreground">Current Cost</div>
                    <div className="text-xs text-gray-500">After optimizations</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">30.5%</div>
                    <div className="text-sm text-muted-foreground">Savings Rate</div>
                    <div className="text-xs text-gray-500">Total reduction</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-3">Optimization Categories</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Model Selection</span>
                      <span className="text-sm font-mono">$523.45/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Prompt Optimization</span>
                      <span className="text-sm font-mono">$156.23/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Caching Strategy</span>
                      <span className="text-sm font-mono">$347.82/month</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Request Optimization</span>
                      <span className="text-sm font-mono">$234.67/month</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}